/**
 * Centralized money / currency helpers.
 *
 * One source of truth for:
 *  - the list of ISO currency codes the picker exposes
 *  - mapping a code → short display symbol (USD → "$", BRL → "R$")
 *  - formatting an amount for display (`formatMoney(100, "BRL")` → "R$ 100,00")
 *  - parsing what the user types in a MoneyInput back into a number
 *
 * Use this everywhere the app shows or accepts money so the business's
 * configured currency is respected end-to-end.
 */

export interface CurrencyOption {
    /** ISO 4217 code (USD, BRL, EUR...). */
    code: string;
    /** Short symbol shown as a prefix in inputs ($, R$, €...). */
    symbol: string;
    /** Friendly label for the picker. */
    label: string;
    /** BCP-47 locale used by Intl.NumberFormat for nice formatting. */
    locale: string;
}

/**
 * Currencies the app supports out of the box. Add more here when needed —
 * everything that consumes money will pick them up automatically.
 */
export const CURRENCIES: CurrencyOption[] = [
    { code: 'USD', symbol: '$',   label: 'USD — US Dollar',          locale: 'en-US' },
    { code: 'BRL', symbol: 'R$',  label: 'BRL — Brazilian Real',     locale: 'pt-BR' },
    { code: 'EUR', symbol: '€',   label: 'EUR — Euro',               locale: 'de-DE' },
    { code: 'GBP', symbol: '£',   label: 'GBP — British Pound',      locale: 'en-GB' },
    { code: 'CAD', symbol: 'CA$', label: 'CAD — Canadian Dollar',    locale: 'en-CA' },
    { code: 'AUD', symbol: 'A$',  label: 'AUD — Australian Dollar',  locale: 'en-AU' },
    { code: 'MXN', symbol: 'MX$', label: 'MXN — Mexican Peso',       locale: 'es-MX' },
    { code: 'ARS', symbol: 'AR$', label: 'ARS — Argentine Peso',     locale: 'es-AR' },
    { code: 'CLP', symbol: 'CL$', label: 'CLP — Chilean Peso',       locale: 'es-CL' },
    { code: 'COP', symbol: 'CO$', label: 'COP — Colombian Peso',     locale: 'es-CO' },
    { code: 'JPY', symbol: '¥',   label: 'JPY — Japanese Yen',       locale: 'ja-JP' },
    { code: 'CNY', symbol: '¥',   label: 'CNY — Chinese Yuan',       locale: 'zh-CN' },
    { code: 'CHF', symbol: 'CHF', label: 'CHF — Swiss Franc',        locale: 'de-CH' },
];

const BY_CODE: Record<string, CurrencyOption> = CURRENCIES.reduce(
    (acc, c) => {
        acc[c.code] = c;
        return acc;
    },
    {} as Record<string, CurrencyOption>
);

/**
 * Resolve a code (case-insensitive) into a CurrencyOption.
 * Falls back to USD so the UI never breaks if a business has a legacy code.
 * Legacy aliases: "REAL" (old value pre-ISO migration) → BRL.
 */
export const getCurrency = (
    codeOrNull: string | null | undefined
): CurrencyOption => {
    if (!codeOrNull) return BY_CODE['USD'];
    const upper = codeOrNull.toUpperCase().trim();
    if (upper === 'REAL') return BY_CODE['BRL'];
    return BY_CODE[upper] ?? BY_CODE['USD'];
};

/** Short symbol (e.g. "R$"). Convenience wrapper. */
export const currencySymbol = (codeOrNull: string | null | undefined): string =>
    getCurrency(codeOrNull).symbol;

/** ISO code (e.g. "BRL"), normalized + falling back to USD. */
export const currencyCode = (codeOrNull: string | null | undefined): string =>
    getCurrency(codeOrNull).code;

/**
 * Format an amount the standard way for the given currency. Uses
 * Intl.NumberFormat with the currency's natural locale, which means
 * BRL → "R$ 100,00", USD → "$100.00", EUR → "100,00 €", etc.
 */
export const formatMoney = (
    amount: number | null | undefined,
    codeOrNull: string | null | undefined
): string => {
    const v = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
    const cur = getCurrency(codeOrNull);
    try {
        return new Intl.NumberFormat(cur.locale, {
            style: 'currency',
            currency: cur.code,
        }).format(v);
    } catch {
        // If for any reason the runtime doesn't support that locale/currency,
        // fall back to a manual symbol + 2-decimal render.
        return `${cur.symbol}${v.toFixed(2)}`;
    }
};

/**
 * Parse a user-typed money string ("1.234,56", "1,234.56", "100,00",
 * "100.00", "R$ 100") into a number. Returns NaN if unparseable so the
 * caller can decide what to do.
 */
export const parseMoney = (raw: string | null | undefined): number => {
    if (raw == null) return NaN;
    let s = String(raw).trim();
    if (!s) return NaN;
    // Strip currency symbols, letters and whitespace.
    s = s.replace(/[^0-9.,-]/g, '');
    if (!s) return NaN;
    // Determine whether the last separator is decimal. If both `,` and `.`
    // appear, the one rightmost is decimal — strip the other as thousands.
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    if (lastComma > -1 && lastDot > -1) {
        if (lastComma > lastDot) {
            s = s.replace(/\./g, '').replace(',', '.');
        } else {
            s = s.replace(/,/g, '');
        }
    } else if (lastComma > -1) {
        s = s.replace(',', '.');
    }
    const n = parseFloat(s);
    return isNaN(n) ? NaN : n;
};
